import { useEffect, useState } from "react";
import {
  getFacilities,
  getMyAttorneyAccess,
  requestAttorneyAccess,
} from "../api/attorneyAccess";
import AttorneyNav from "../components/AttorneyNav";
import AsyncButton from "../components/AsyncButton";
import StatusMessage from "../components/StatusMessage";
import { getErrorMessage } from "../utils/apiErrors";

export default function AttorneyAccess() {
  const [facilities, setFacilities] = useState([]);
  const [accesses, setAccesses] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadData = async () => {
    setLoading(true);

    try {
      const [facilityRes, accessRes] = await Promise.all([
        getFacilities(),
        getMyAttorneyAccess(),
      ]);

      setFacilities(facilityRes.data.data || []);
      setAccesses(accessRes.data.data || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load facility access."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRequest = async (e) => {
    e.preventDefault();

    if (!facilityId) {
      setError("Select a facility.");
      return;
    }

    if (submitting) return;
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      await requestAttorneyAccess(facilityId);
      setFacilityId("");
      setMessage("Facility access request submitted for review.");
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-shell">
      <div className="topbar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div className="brand-mark">CV</div>
          <div>
            <h5 className="mb-0">CounselVault</h5>
            <div className="small opacity-75">Attorney Facility Access</div>
          </div>
        </div>
        <AttorneyNav />
      </div>

      <main className="page-wrap">
        <StatusMessage type="success">{message}</StatusMessage>
        <StatusMessage type="warning">{error}</StatusMessage>
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <h2 className="mb-1">Facility Access</h2>
            <p className="text-muted mb-0">
              Request approval to submit legal documents to a correctional facility.
            </p>
          </div>

          <div className="stat-card px-4 py-3 text-end">
            <div className="text-muted small">Requests</div>
            <h3 className="mb-0">{accesses.length}</h3>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-5">
            <div className="portal-card">
              <div className="portal-card-header">
                <h5 className="mb-0">Request Facility Access</h5>
                <div className="text-muted small">
                  Facility staff must approve access before uploads are allowed.
                </div>
              </div>

              <div className="portal-card-body">
                <form onSubmit={handleRequest}>
                  <div className="mb-4">
                    <label className="form-label" htmlFor="facility-access-id">Facility</label>
                    <select
                      id="facility-access-id"
                      className="form-select form-select-lg"
                      value={facilityId}
                      onChange={(e) => setFacilityId(e.target.value)}
                    >
                      <option value="">Select facility</option>
                      {facilities.map((facility) => (
                        <option key={facility.id} value={facility.id}>
                          {facility.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <AsyncButton className="btn btn-dark btn-lg w-100" busy={submitting} busyText="Submitting…">
                    Submit Access Request
                  </AsyncButton>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-7">
            <div className="portal-card">
              <div className="portal-card-header d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">My Facility Access</h5>
                  <div className="text-muted small">
                    View pending, approved, rejected, or revoked access.
                  </div>
                </div>

                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={loadData}
                >
                  Refresh
                </button>
              </div>

              <div className="portal-card-body">
                {loading ? (
                  <p className="mb-0">Loading access...</p>
                ) : accesses.length === 0 ? (
                  <div className="text-center py-5">
                    <h5>No access requests</h5>
                    <p className="text-muted mb-0">
                      Submit a request to begin uploading documents.
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Facility</th>
                          <th>Status</th>
                          <th>Requested</th>
                        </tr>
                      </thead>

                      <tbody>
                        {accesses.map((access) => (
                          <tr key={access.id}>
                            <td>
                              <strong>{access.facility?.name || "Facility"}</strong>
                            </td>

                            <td>
                              <span className={`status-pill status-${access.status}`}>
                                {access.status}
                              </span>
                            </td>

                            <td className="text-muted small">
                              {access.created_at || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
